import React, { useMemo } from 'react';

import { Box, Chip, Skeleton, Tooltip, Typography } from '@mui/material';

import useHeroPlannerOfferVisibilities from '~/hooks/customerCommunication/useHeroPlannerOfferVisibilities';

import { Dayjs } from '~/services/TimeService';

import { isRequestUnresolved } from '~/utils/requestUtils';

import { isLuxPlusMonthCampaign } from '../../utils/heroPlannerScheduleUtils';

interface Props {
  scheduleSendDate: Dayjs;
  countryGroupId: string;
  countryId: string;
  membershipId: string;
  offerId: string;
  campaign?: string;
}

function isLuxPlusMembership(membershipId: string) {
  return membershipId === 'LUXPLUS';
}

function ScheduleOfferVisibilityBlock({
  scheduleSendDate,
  countryGroupId,
  countryId,
  membershipId,
  offerId,
  campaign,
}: Props) {
  const { offerVisibilitiesReq } = useHeroPlannerOfferVisibilities(countryGroupId, countryId, offerId);
  const isCampaignLuxPlusMonth = isLuxPlusMonthCampaign(campaign, scheduleSendDate);

  const visibilities = useMemo(() => {
    if (!offerVisibilitiesReq || isRequestUnresolved(offerVisibilitiesReq)) {
      return;
    }

    return Object.values(offerVisibilitiesReq.result).map((v) => {
      const isLiveOnSendDate = scheduleSendDate.isBetween(v.start, v.end, 'date', '[]');
      let dateRange = v.start && v.end ? `${v.start.format('DD/MM/YYYY')} — ${v.end.format('DD/MM/YYYY')}` : undefined;
      let isLuxPlusPriorityAccess = false;

      if (isLiveOnSendDate) {
        return {
          countryId: v.countryId,
          dateRange,
          isLuxPlusPriorityAccess,
          shouldShowError: !isLiveOnSendDate && !dateRange,
        };
      }

      // Check for priority access
      const isStartInFuture = v.start ? v.start?.isAfter(scheduleSendDate, 'date') : true;
      if (isStartInFuture && v.luxPlusVisibilityStart && v.luxPlusVisibilityEnd) {
        isLuxPlusPriorityAccess = scheduleSendDate.isBetween(
          v.luxPlusVisibilityStart,
          v.luxPlusVisibilityEnd,
          'date',
          '[]',
        );
        dateRange = isLuxPlusPriorityAccess
          ? `${v.luxPlusVisibilityStart.format('DD/MM/YYYY')} — ${v.luxPlusVisibilityEnd.format('DD/MM/YYYY')}`
          : dateRange;
      }

      return {
        countryId: v.countryId,
        dateRange,
        isLuxPlusPriorityAccess,
        // LuxPlus+ Month campaign offers are unlocked for everyone during the campaign window,
        // so the priority-access membership warning no longer applies to them.
        shouldShowError:
          !isLuxPlusPriorityAccess ||
          !dateRange ||
          (isLuxPlusPriorityAccess && !isLuxPlusMembership(membershipId) && !isCampaignLuxPlusMonth),
      };
    });
  }, [offerVisibilitiesReq, scheduleSendDate, membershipId, isCampaignLuxPlusMonth]);

  if (!visibilities) {
    return (
      <Typography variant="body2">
        <Skeleton width={192} />
      </Typography>
    );
  }

  return (
    <Box>
      {visibilities.map((visibility) => {
        return (
          <Box key={visibility.countryId} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" color={visibility.shouldShowError ? 'error' : undefined}>
              <span>{visibility.countryId}: </span>
              {!!visibility.dateRange && <b>{visibility.dateRange}</b>}
              {!visibility.dateRange && <i>Unavailable</i>}
            </Typography>
            {visibility.isLuxPlusPriorityAccess && (
              <Tooltip title="Lux Plus Priority Access">
                <Chip label="LP+" size="small" color="info" />
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default ScheduleOfferVisibilityBlock;
